import { Viewer } from '@/components';
import FullPageError from '@/components/FullPageError';
import FullPageLoader from '@/components/FullPageLoader';
import SkygisCaption from '@/components/SkygisCaption';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ViewerSettingsDialog } from '@/components/ViewerSettingsDialog';
import { buildOrgUrl, isAppDomain } from '@/core/domain';
import { execute } from '@/graphql/execute';
import { usePageMeta } from '@/hooks/usePageMeta';
import PageSkeleton, { iAction } from '@/layout/Skeleton';
import { analytics } from '@/lib/analytics';
import { getSharedProjectQuery } from '@/queries/project.query';
import { CogFourSolid, LayersThree } from '@mynaui/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { FunctionComponent, Suspense, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';

interface iSharedPageProps {}

const SharedPage: FunctionComponent<iSharedPageProps> = () => {
  const { t } = useTranslation('common');
  const { projectId } = useParams();
  if (!projectId) throw new Error('Token is required');

  const [params, setParams] = useSearchParams();
  const [isSettingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const isLayersEnabled = params.get('layers') === 'true';

  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['sharedProject', projectId],
    queryFn: () => execute(getSharedProjectQuery),
    retry: false,
  });

  const isPasswordRequired = error?.message === 'PASSWORD_REQUIRED';

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) return;

    setIsVerifying(true);
    setPasswordError('');

    sessionStorage.setItem('link-password', password);

    try {
      await queryClient.fetchQuery({
        queryKey: ['sharedProject', projectId],
        queryFn: () => execute(getSharedProjectQuery),
      });
    } catch (err: any) {
      sessionStorage.removeItem('link-password');
      if (err?.message === 'PASSWORD_REQUIRED') {
        setPasswordError(t('shared.invalidPassword'));
      } else if (err?.message === 'Network response was not ok') {
        setPasswordError(t('shared.invalidPassword'));
      } else {
        setPasswordError(t('shared.failedVerifyPassword'));
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const project = data?.sharedProject;
  usePageMeta({ title: [project?.name] });

  useEffect(() => {
    if (project) {
      analytics.capture('Shared Project Viewed');
    }
  }, [project]);

  useEffect(() => {
    if (!project?.organization?.slug) return;
    if (!isAppDomain()) return;
    const path = `/shared/${projectId}${window.location.search}`;
    window.location.href = buildOrgUrl(project.organization.slug, path);
  }, [project?.organization?.slug, projectId]);

  const actions: iAction[] = [
    {
      icon: <LayersThree />,
      onClick: () => {
        const newParams = new URLSearchParams(window.location.search);
        newParams.set('layers', isLayersEnabled ? 'false' : 'true');
        setParams(newParams);
      },
      tooltip: t('shared.toggleLayers'),
      appearance: isLayersEnabled ? 'primary' : 'secondary',
    },
    {
      icon: <CogFourSolid />,
      onClick: () => setSettingsDialogOpen(true),
      tooltip: t('shared.viewerSettings'),
      appearance: 'secondary',
    },
  ];

  if (isLoading) return <FullPageLoader />;

  if (isPasswordRequired) {
    return (
      <div className='h-full flex items-center justify-center'>
        <form onSubmit={handlePasswordSubmit} className='w-full max-w-sm space-y-4 p-6'>
          <div className='space-y-2 text-center'>
            <h1 className='text-xl font-semibold'>{t('shared.passwordRequired')}</h1>
            <p className='text-sm text-muted-foreground'>{t('shared.passwordProtected')}</p>
          </div>
          <div className='space-y-2'>
            <Input
              type='password'
              placeholder={t('shared.enterPassword')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isVerifying}
              autoFocus
            />
            {passwordError && <p className='text-sm text-destructive'>{passwordError}</p>}
          </div>
          <Button type='submit' className='w-full' disabled={isVerifying || !password}>
            {isVerifying ? <Loader2 className='size-4 animate-spin' /> : t('shared.unlock')}
          </Button>
        </form>
      </div>
    );
  }

  if (error) {
    return (
      <FullPageError
        error={error}
        title={t('shared.failedLoadProject')}
        subtitle={t('shared.failedLoadProjectDescription')}
      />
    );
  }
  return (
    <div className='h-full flex flex-col'>
      <PageSkeleton
        hideSidebar
        branding={<SkygisCaption className='h-6' />}
        steps={[{ label: project?.name || t('shared.loading'), href: `/shared/${projectId}` }]}
        actions={actions}>
        <div className='flex-1 flex flex-col'>
          <Suspense fallback={<div>{t('shared.loading')}</div>}>
            <ErrorBoundary key={project?.id} fallback={<div>{t('shared.error')}</div>}>
              <Viewer projectId={project?.id!} />
            </ErrorBoundary>
          </Suspense>
        </div>
      </PageSkeleton>
      {isSettingsDialogOpen && (
        <ViewerSettingsDialog open={isSettingsDialogOpen} onOpenChange={setSettingsDialogOpen} />
      )}
    </div>
  );
};

export default SharedPage;
